import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { buildCommissionsRequestDto, mapCommissionsResponseToDto } from '../dto/commissions-list.dto.js';
import { buildCommissionsTableModel } from '../model/commissions-list.model.js';

export function useCommissionsList() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const loadCommissions = useCallback(async (filters = {}) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await adminApi.getCommissions(token, buildCommissionsRequestDto(filters));
      const dto = mapCommissionsResponseToDto(response);
      setRows(buildCommissionsTableModel(dto));
    } catch (requestError) {
      setRows([]);
      setError(requestError.message || 'Failed to load commissions');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadCommissions({ type: typeFilter, status: statusFilter });
  }, [loadCommissions, typeFilter, statusFilter]);

  const toggleFilter = useCallback(() => { setIsFilterOpen((prev) => !prev); }, []);

  const resetFilters = useCallback(() => {
    setTypeFilter(''); setStatusFilter(''); setIsFilterOpen(false);
  }, []);

  const reload = useCallback(() => {
    loadCommissions({ type: typeFilter, status: statusFilter });
  }, [loadCommissions, typeFilter, statusFilter]);

  const approveCommission = useCallback(async (uid) => {
    setIsSaving(true); setError(''); setSuccess('');
    try {
      await adminApi.approveCommission(token, uid);
      setSuccess('Commission approved');
      await reload();
    } catch (requestError) {
      setError(requestError.message || 'Failed to approve commission');
    } finally { setIsSaving(false); }
  }, [token, reload]);

  const cancelCommission = useCallback(async (uid) => {
    setIsSaving(true); setError(''); setSuccess('');
    try {
      await adminApi.cancelCommission(token, uid);
      setSuccess('Commission cancelled');
      await reload();
    } catch (requestError) {
      setError(requestError.message || 'Failed to cancel commission');
    } finally { setIsSaving(false); }
  }, [token, reload]);

  return {
    rows, isLoading, isSaving, error, success,
    typeFilter, setTypeFilter, statusFilter, setStatusFilter,
    isFilterOpen, toggleFilter, resetFilters, reload,
    approveCommission, cancelCommission,
  };
}
